import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  EmptyState,
} from '@/components/ui';

interface ScoredProperty {
  apn: string;
  address: string;
  city: string;
  county: string;
  state: string;
  zip?: string;
  owner_name?: string;
  land_area?: number;
  building_area?: number;
  year_built?: number;
  property_class?: string;
  assessed_value?: number;
  score: number;
  reasons: string[];
}

interface ReportResult {
  success: boolean;
  report: {
    criteria: { name: string; description: string };
    totalMatches: number;
    generatedAt: string;
  };
  properties: ScoredProperty[];
}

const REPORT_TYPES = [
  { id: 'roof_replacement', name: 'Roof Replacement Needed', icon: '🔨' },
  { id: 'solar_potential', name: 'Solar Panel Candidates', icon: '☀️' },
  { id: 'high_value', name: 'High Value Properties', icon: '💰' },
  { id: 'aging_roof', name: 'Aging Roof Alert', icon: '⚠️' },
  { id: 'large_lot', name: 'Large Lot Properties', icon: '🏡' },
];

export default function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState<string>('roof_replacement');
  const [reportResult, setReportResult] = useState<ReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const generateReport = async (type: string) => {
    setSelectedReport(type);
    setIsLoading(true);
    try {
      const response = await fetch('https://prisai-api.cyopsys.workers.dev/api/reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'pra_idbuCt7x7GQju0tRfqAw',
        },
        body: JSON.stringify({ type, limit: 100 }),
      });
      const data = await response.json() as ReportResult;
      setReportResult(data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportCSV = () => {
    if (!reportResult?.properties) return;
    
    const headers = ['Score', 'Address', 'City', 'State', 'Owner', 'APN', 'Value', 'Year Built', 'Reasons'];
    const rows = reportResult.properties.map(p => [
      p.score,
      p.address,
      `${p.city}, ${p.state}`,
      p.owner_name || '',
      p.apn,
      p.assessed_value || 0,
      p.year_built || 0,
      `"${p.reasons.join('; ')}"`,
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}-report-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Property Reports</h1>
        <p className="text-gray-600">Generate special criteria reports for sales leads</p>
      </div>

      {/* Report Type Selection */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold">Select Report Type</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORT_TYPES.map((report) => (
              <button
                key={report.id}
                onClick={() => generateReport(report.id)}
                disabled={isLoading}
                className={`p-4 rounded-lg border text-left transition-all ${
                  selectedReport === report.id
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{report.icon}</span>
                  <div>
                    <div className="font-medium text-gray-900">{report.name}</div>
                    <div className="text-sm text-gray-500">
                      {report.id === 'roof_replacement' && 'Properties with aging roofs'}
                      {report.id === 'solar_potential' && 'Good candidates for solar'}
                      {report.id === 'high_value' && 'Premium properties'}
                      {report.id === 'aging_roof' && '15+ year old roofs'}
                      {report.id === 'large_lot' && 'Big lots for development'}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Report Results */}
      {reportResult && (
        <>
          <Card>
            <CardHeader className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{reportResult.report.criteria.name}</h3>
                <p className="text-sm text-gray-500">
                  {reportResult.report.criteria.description} • {reportResult.report.totalMatches} matches
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportCSV}>
                  Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => setReportResult(null)}>
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {reportResult.properties.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Score</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportResult.properties.map((property, idx) => (
                      <TableRow key={idx} className={property.score >= 50 ? 'bg-green-50' : ''}>
                        <TableCell>
                          <span className={`text-lg font-bold ${
                            property.score >= 50 ? 'text-green-600' :
                            property.score >= 30 ? 'text-blue-600' : 'text-gray-600'
                          }`}>
                            {property.score}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{property.address}</div>
                          <div className="text-sm text-gray-500">
                            {property.city}, {property.state} {property.zip}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            APN: {property.apn}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{property.owner_name || '-'}</div>
                          <Badge variant="default" className="mt-1">
                            {property.property_class || 'Residential'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {property.assessed_value 
                            ? <span className="font-medium">${property.assessed_value.toLocaleString()}</span>
                            : '-'}
                          <div className="text-xs text-gray-500 mt-1">
                            Built: {property.year_built || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {property.reasons.slice(0, 2).map((reason, i) => (
                              <div key={i} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                {reason}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState
                  icon="📊"
                  title="No properties match criteria"
                  description="Try a different report type or scrape more data"
                />
              )}
            </CardBody>
          </Card>

          {/* Summary Stats */}
          <Card>
            <CardHeader>
              <h3 className="font-semibold">Report Summary</h3>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">
                    {reportResult.properties.length}
                  </div>
                  <div className="text-sm text-blue-600">Total Properties</div>
                </div>
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <div className="text-2xl font-bold text-green-600">
                    {reportResult.properties.filter(p => p.score >= 50).length}
                  </div>
                  <div className="text-sm text-green-600">High Priority</div>
                </div>
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">
                    ${reportResult.properties.reduce((sum, p) => sum + (p.assessed_value || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-yellow-600">Total Value</div>
                </div>
                <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">
                    {reportResult.properties.filter(p => p.year_built && p.year_built < 2000).length}
                  </div>
                  <div className="text-sm text-purple-600">Pre-2000 Built</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </>
      )}

      {/* Info Card */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold">Report Types Explained</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="font-medium">🔨 Roof Replacement Needed</div>
              <p className="text-gray-600 mt-1">
                Properties where the roof is 20+ years old. These owners may be interested in 
                roof replacement services.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="font-medium">☀️ Solar Panel Candidates</div>
              <p className="text-gray-600 mt-1">
                Properties with good solar potential based on lot size, property value, and 
                lack of existing solar installations.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="font-medium">💰 High Value Properties</div>
              <p className="text-gray-600 mt-1">
                Properties assessed over $500,000. These owners typically have higher 
                disposable income.
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="font-medium">⚠️ Aging Roof Alert</div>
              <p className="text-gray-600 mt-1">
                Properties where the roof is 15+ years old. Good for preventive outreach 
                before major issues develop.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
